import React, { useCallback, useMemo, useState } from 'react';
import { GraphsData, GraphPoint, TimeFrame } from '@hud/portal-dtos/webapp-common.dto';
import { Endpoint, FlowDeployments } from '@hud/portal-dtos/webapp-endpoint.dto';
import { PublicIssue } from '@hud/portal-dtos/issues-v2.dtos';
import { useElementSize } from '@mantine/hooks';
import {
  formatBigNumbers,
  formatDateShort,
  formatDateTime,
  formatDuration,
  formatNumber,
  formatPercentage,
  formatTime12Hour,
  formatTime12HourWithDay,
  isTimeFrameLessThanDay,
  isTimeFrameLessThan2Days,
} from '@hud/web-components/utils';
import { maxBy } from 'lodash';
import Metric from '@hud/web-components/Metric/Metric';
import { useTheme } from 'styled-components';
import { MetricHeader } from '@hud/web-components/Hud/FunctionBehaviorChart/MetricHeader';
import { FilterMenuButton } from '@hud/web-components/Hud/FunctionBehaviorChart/FilterMenuButton';
import {
  DataKey,
  DataKeyDef,
  XReferenceLine,
} from '@hud/web-components/Charts/LineChart/LineChart';
import ReferenceIndicator from '@hud/web-components/Charts/ReferenceIndicator/ReferenceIndicator';
import { FunctionBehaviorContainer, FunctionsLineChart } from './EndpointPage.styles';
import {
  DeploymentsList,
  ReferenceIndicatorPopover,
} from '@hud/web-components/Hud/FunctionBehaviorChart/FunctionBehaviorChart.styles';
import {
  P90_TOOLTIP,
  P90_TOOLTIP_URL,
} from '@hud/web-components/Hud/P90InfoHover/P90InfoHover';
import useTimeFrame from '../../../hooks/useTimeFrame';
import { useBooleanFeatureFlag } from '../../../hooks/useFeatureFlags';

export interface EndpointBehaviorProps {
  endpoint?: Endpoint;
  graph?: GraphsData;
  deployments?: FlowDeployments;
  issues?: PublicIssue[];
  loading: boolean;
  compact?: boolean;
  timeFrame: TimeFrame;
  firstSeenAt?: Date;
}

const SINGLE_THRESHOLD_INTERVAL = 2;

const roundToBeginningOfHour = (date: Date): Date => {
  const newDate = new Date(date.getTime()); // Clone the original date
  newDate.setMinutes(0, 0, 0); // Sets minutes, seconds, and milliseconds to zero
  return newDate;
};

export default function EndpointBehavior({
  endpoint,
  graph,
  deployments,
  issues,
  loading,
  compact = false,
  timeFrame,
  firstSeenAt,
}: EndpointBehaviorProps) {
  const theme = useTheme();
  const { ref, width } = useElementSize();
  const { setTimeFrame } = useTimeFrame();
  const [hiddenDurationKeys, setHiddenDurationKeys] = useState<string[]>([]);
  const showDeployments = useBooleanFeatureFlag('show-deployments', true);
  const showIssuesInCharts = useBooleanFeatureFlag('show-issues-in-charts', true);

  const maxInvocations = maxBy(graph, 'runs')?.runs || 0;
  const maxDuration = Math.max(
    maxBy(graph, 'duration')?.duration || 0,
    maxBy(graph, 'durationP90')?.durationP90 || 0,
  );
  const maxErrorRate = maxBy(graph, 'errorRate')?.errorRate || 0;

  const isMaxInvocationsThreshold = maxInvocations < 0.01;
  const isMaxDurationThreshold = maxDuration < 0.00000001;
  const isMaxErrorRateThreshold = maxErrorRate < 0.01;

  const normalizedGraph = useMemo<GraphsData>(() => {
    const normalized =
      graph?.map((g) => {
        const firstSeenAt = endpoint?.firstSeenAt
          ? roundToBeginningOfHour(endpoint.firstSeenAt)
          : null;
        if (firstSeenAt && g.timestamp < firstSeenAt) {
          return {
            timestamp: g.timestamp,
            runs: null,
            duration: null,
            durationP90: null,
            errorRate: null,
          } satisfies GraphPoint;
        }
        return {
          timestamp: g.timestamp,
          runs: isMaxInvocationsThreshold
            ? g.runs === 0 || g.runs === null
              ? g.runs
              : maxInvocations
            : g.runs,
          duration: isMaxDurationThreshold
            ? g.duration === 0 || g.duration === null
              ? g.duration
              : maxDuration
            : g.duration,
          durationP90: isMaxDurationThreshold
            ? g.durationP90 === 0 || g.durationP90 === null || g.durationP90 === undefined
              ? g.durationP90
              : maxDuration
            : g.durationP90,
          errorRate: isMaxErrorRateThreshold
            ? g.errorRate === 0 || g.errorRate === null
              ? g.errorRate
              : maxErrorRate
            : g.errorRate,
        } satisfies GraphPoint;
      }) ?? [];
    return normalized;
  }, [
    endpoint,
    graph,
    isMaxDurationThreshold,
    isMaxErrorRateThreshold,
    isMaxInvocationsThreshold,
    maxDuration,
    maxErrorRate,
    maxInvocations,
  ]);

  const references = useMemo(() => {
    if (!graph || graph.length < 2) return [];

    // Calculate the interval between the first two graph points
    const interval = graph[1].timestamp.getTime() - graph[0].timestamp.getTime();

    return graph
      .map((g) => {
        const relevantDeployments =
          showDeployments && deployments
            ? deployments.filter((deployment) => {
                const deploymentTime = deployment.timestamp.getTime();
                const graphTime = g.timestamp.getTime();
                return (
                  deploymentTime >= graphTime && deploymentTime < graphTime + interval
                );
              })
            : [];

        const relevantIssues =
          showIssuesInCharts && issues
            ? issues.filter((issue) => {
                const issueTime = new Date(issue.detectionTime).getTime();
                const graphTime = g.timestamp.getTime();
                return issueTime >= graphTime && issueTime < graphTime + interval;
              })
            : [];

        const type =
          relevantIssues.length > 0
            ? relevantDeployments.length > 0
              ? ('part-warning' as const)
              : ('warning' as const)
            : undefined;

        return {
          xAxisValue: g.timestamp,
          deployments: relevantDeployments,
          issues: relevantIssues,
          type,
        };
      })
      .filter(
        (reference) => reference.deployments.length > 0 || reference.issues.length > 0,
      ); // Filter out empty references
  }, [deployments, issues, graph, showDeployments, showIssuesInCharts]);

  function toggleHiddenDurationKey(key: string) {
    if (hiddenDurationKeys.includes(key)) {
      setHiddenDurationKeys(hiddenDurationKeys.filter((k) => k !== key));
    } else {
      setHiddenDurationKeys([...hiddenDurationKeys, key]);
    }
  }

  const durationDataKeys: DataKey[] = [
    {
      key: 'duration',
      label: 'Avg Dur',
      hidden: hiddenDurationKeys.includes('duration'),
    },
    {
      key: 'durationP90',
      color: theme.purpleHome,
      label: 'P90 Dur',
      hidden: hiddenDurationKeys.includes('durationP90'),
    },
  ];

  const isLessThan2Days = isTimeFrameLessThan2Days(timeFrame, firstSeenAt);

  const renderReferenceLine = useCallback((value: XReferenceLine) => {
    const deployments = value.deployments as FlowDeployments;
    const issues = value.issues as PublicIssue[];

    // Handle deployments
    let deploymentContent = null;
    if (deployments && deployments.length > 0) {
      const functionsDeployed = deployments.reduce(
        (acc, deployment) => acc + deployment.functionIds.length,
        0,
      );
      const timestampDiv =
        deployments.length === 1 ? (
          <div>{formatDateTime(deployments[0].timestamp)}</div>
        ) : (
          <div>
            {formatDateTime(deployments[0].timestamp)} -{' '}
            {formatDateTime(deployments[deployments.length - 1].timestamp)}
          </div>
        );
      const deploymentMessage = `${functionsDeployed} function${functionsDeployed > 1 ? 's' : ''} deployed at`;
      deploymentContent = (
        <div>
          <div>{deploymentMessage}</div>
          {timestampDiv}
        </div>
      );
    }

    // Handle issues
    let issueContent = null;
    if (issues && issues.length > 0) {
      const timestampDiv =
        issues.length === 1 ? (
          <div>{formatDateTime(issues[0].detectionTime)}</div>
        ) : (
          <div>
            {formatDateTime(issues[0].detectionTime)} -{' '}
            {formatDateTime(issues[issues.length - 1].detectionTime)}
          </div>
        );
      const issueMessage = `${issues.length} issue${issues.length > 1 ? 's' : ''} detected at`;
      issueContent = (
        <div>
          <div>{issueMessage}</div>
          {timestampDiv}
        </div>
      );
    }

    // Combine content
    const popoverBody = (
      <div>
        {deploymentContent}
        {deploymentContent && issueContent && <div style={{ marginTop: '8px' }} />}
        {issueContent}
      </div>
    );

    const totalCount = (deployments?.length || 0) + (issues?.length || 0);
    const hasMultipleItems =
      (deployments && deployments.length > 1) || (issues && issues.length > 1);

    const clickBody = (
      <DeploymentsList>
        {deployments &&
          hasMultipleItems &&
          deployments.map((deployment, index) => (
            <div key={`deployment-${index}`}>
              Deployment: {formatDateTime(deployment.timestamp)}
            </div>
          ))}
        {issues &&
          hasMultipleItems &&
          issues.map((issue, index) => (
            <div key={`issue-${index}`}>
              Issue: {formatDateTime(issue.detectionTime)} - {issue.title}
            </div>
          ))}
      </DeploymentsList>
    );

    return (
      <ReferenceIndicatorPopover
        body={popoverBody}
        clickBody={hasMultipleItems ? clickBody : null}
      >
        <ReferenceIndicator
          count={totalCount}
          type={value.type}
        />
      </ReferenceIndicatorPopover>
    );
  }, []);

  const dataKeyDefs: DataKeyDef[] = [
    {
      title: 'Invocations',
      yInterval: isMaxInvocationsThreshold ? SINGLE_THRESHOLD_INTERVAL : undefined,
      labelFormatter: (value: number) => formatBigNumbers(value),
      tooltipFormatter: (value: number) => formatNumber(value),
      dataKeys: [{ key: 'runs' }],
      headerComponent: (
        <MetricHeader
          loading={loading}
          metrics={[
            {
              title: 'Total:',
              metric: <Metric metric={endpoint?.metrics.runs} unit="abbreviated" />,
            },
            ...(!isTimeFrameLessThanDay(timeFrame, firstSeenAt)
              ? [
                  {
                    title: 'Runs/Day:',
                    metric: (
                      <Metric
                        metric={endpoint?.metrics.normalizedRuns}
                        showTrend
                        trendTooltip="The trend change in how many times the endpoint is invoked per day - last 24h vs last 7d"
                        unit="abbreviated"
                      />
                    ),
                  },
                ]
              : []),
          ]}
        />
      ),
    },
    {
      title: (
        <>
          Duration
          <FilterMenuButton
            dataKeys={durationDataKeys}
            hiddenKeys={hiddenDurationKeys}
            onToggle={toggleHiddenDurationKey}
          />
        </>
      ),
      dataKeys: durationDataKeys,
      yInterval: isMaxDurationThreshold ? SINGLE_THRESHOLD_INTERVAL : undefined,
      labelFormatter: (value: number) => formatDuration(value, 0),
      tooltipFormatter: (value: number) => formatDuration(value, 0),
      headerComponent: (
        <MetricHeader
          loading={loading}
          metrics={[
            {
              title: 'Avg:',
              color: theme.turquoise,
              hidden: hiddenDurationKeys.includes('duration'),
              metric: (
                <Metric
                  metric={endpoint?.metrics.durationAvg}
                  unit="duration"
                  showTrend
                  trendTooltip="The trend change in the average duration of the endpoint - last 24h vs last 7d"
                />
              ),
            },
            {
              title: 'P90:',
              tooltip: P90_TOOLTIP,
              tooltipUrl: P90_TOOLTIP_URL,
              color: theme.purpleHome,
              hidden: hiddenDurationKeys.includes('durationP90'),
              metric: (
                <Metric
                  metric={endpoint?.metrics.durationP90}
                  unit="duration"
                  showTrend
                  trendTooltip="The trend change in the P90 duration of the endpoint - last 24h vs last 7d"
                />
              ),
            },
          ]}
        />
      ),
    },
    {
      title: 'Error Rate',
      domain: [0, maxErrorRate === 0 ? 1 : 'auto'],
      dataKeys: [{ key: 'errorRate' }],
      labelFormatter: (value: number) => formatPercentage(value),
      tooltipFormatter: (value: number) => formatPercentage(value),
      headerComponent: (
        <MetricHeader
          loading={loading}
          metrics={[
            {
              title: 'Rate:',
              metric: (
                <Metric
                  metric={endpoint?.metrics.errorRate}
                  unit="percent"
                  showTrend
                  trendTooltip="The trend change in the percentage of endpoint invocations returning 5xx errors - last 24h vs last 7d"
                />
              ),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <FunctionBehaviorContainer ref={ref}>
      <FunctionsLineChart
        compact={compact}
        graphId="EndpointBehavior"
        width={width}
        headerWidth={210}
        height={100}
        interval={isLessThan2Days ? undefined : 23}
        data={normalizedGraph}
        labelFormatter={(value, index) => {
          if (isLessThan2Days) {
            if (index === 0) {
              return formatTime12HourWithDay(new Date(value));
            }
            return formatTime12Hour(new Date(value));
          }
          return formatDateShort(new Date(value));
        }}
        renderReference={renderReferenceLine}
        dataKeyDefs={dataKeyDefs}
        XdataKey="timestamp"
        referenceLines={references}
        onRangeSelected={setTimeFrame}
      />
    </FunctionBehaviorContainer>
  );
}
